/**
 * Catalogue des moyens engageables et de leurs caracteristiques nominales.
 *
 * Les valeurs sont volontairement arrondies : elles visent la credibilite
 * « grand public », pas l'exactitude documentaire. Elles peuvent etre
 * surchargees scenario par scenario.
 */

export const UnitCategory = {
  AERIEN: 'AERIEN',
  TERRESTRE: 'TERRESTRE',
  COMMANDEMENT: 'COMMANDEMENT',
} as const;

export type UnitCategoryId = (typeof UnitCategory)[keyof typeof UnitCategory];

export const UnitKind = {
  /** Bombardier d'eau amphibie, ecope sur un plan d'eau. */
  CANADAIR: 'CANADAIR',
  /** Avion multirole largueur de retardant, recharge sur base aerienne. */
  DASH: 'DASH',
  /** Helicoptere bombardier d'eau, ecopage rapide sur petits points d'eau. */
  HELICOPTERE: 'HELICOPTERE',
  /** Helicoptere de commandement et de reconnaissance. */
  HELICO_COMMANDEMENT: 'HELICO_COMMANDEMENT',
  /** Camion-citerne feux de forets. */
  CCF: 'CCF',
  /** Camion-citerne grande capacite (alimentation). */
  CCGC: 'CCGC',
  /** Groupe d'intervention feux de forets (equipe au sol). */
  GROUPE_SOL: 'GROUPE_SOL',
  /** Engin de genie civil pour ouvrir des lignes d'appui. */
  BULLDOZER: 'BULLDOZER',
  /** Poste de commandement mobile. */
  PC_MOBILE: 'PC_MOBILE',
} as const;

export type UnitKindId = (typeof UnitKind)[keyof typeof UnitKind];

/** Produit largue ou projete par un moyen. */
export const Agent = {
  EAU: 'EAU',
  RETARDANT: 'RETARDANT',
  /** Aucun produit : moyen de commandement ou de genie. */
  AUCUN: 'AUCUN',
} as const;

export type AgentId = (typeof Agent)[keyof typeof Agent];

/** Etat courant d'un moyen engage. */
export const UnitState = {
  /** Disponible sur sa base, non engage. */
  DISPONIBLE: 'DISPONIBLE',
  /** En transit vers la zone d'intervention. */
  EN_ROUTE: 'EN_ROUTE',
  /** Sur zone, en attente d'ordre. */
  EN_ATTENTE: 'EN_ATTENTE',
  /** En action (largage, attaque au sol, etablissement). */
  EN_ACTION: 'EN_ACTION',
  /** En rechargement (ecopage, plein de retardant, alimentation). */
  RECHARGEMENT: 'RECHARGEMENT',
  /** En repos obligatoire (fatigue equipage ou plafond horaire atteint). */
  REPOS: 'REPOS',
  /** Retire de l'engagement (accident, panne, fin de vacation). */
  INDISPONIBLE: 'INDISPONIBLE',
} as const;

export type UnitStateId = (typeof UnitState)[keyof typeof UnitState];

export interface UnitSpec {
  readonly kind: UnitKindId;
  readonly category: UnitCategoryId;
  /** Libelle court affiche dans l'interface (francais). */
  readonly label: string;
  readonly agent: AgentId;
  /** Capacite d'emport en metres cubes (0 pour les moyens sans produit). */
  readonly capacityM3: number;
  /** Vitesse de deplacement en km/h. */
  readonly speedKmh: number;
  /**
   * Duree du remplissage, en secondes de jeu : ecopage sur un plan d'eau pour
   * les amphibies, plein de retardant sur base aerienne pour les autres. Le
   * temps de trajet vers le point de remplissage s'y ajoute, ce qui determine
   * la cadence reelle des rotations.
   */
  readonly reloadSeconds: number;
  /** Autonomie avant retour a la base, en secondes de jeu. */
  readonly enduranceSeconds: number;
  /** Cout d'engagement horaire, en euros (pour le bilan financier de fin de mission). */
  readonly hourlyCostEur: number;
  /** Rayon d'action utile d'un largage ou d'une attaque, en metres. */
  readonly effectRadiusM: number;
  /** Efficacite de base sur l'intensite du feu (0..1). */
  readonly effectiveness: number;
  /** Le moyen peut-il ouvrir une ligne d'appui ? */
  readonly canBuildFireline: boolean;
  /** Le moyen peut-il defendre un batiment ? */
  readonly canDefendStructure: boolean;
  /** Le moyen est-il sensible au vent fort (largage moins precis) ? */
  readonly windSensitive: boolean;
  /** Le moyen peut-il operer de nuit ? */
  readonly nightCapable: boolean;
}

export const UNIT_SPECS: Readonly<Record<UnitKindId, UnitSpec>> = {
  CANADAIR: {
    kind: UnitKind.CANADAIR,
    category: UnitCategory.AERIEN,
    label: 'Canadair',
    agent: Agent.EAU,
    capacityM3: 6,
    speedKmh: 330,
    reloadSeconds: 100,
    enduranceSeconds: 14_400,
    hourlyCostEur: 7000,
    effectRadiusM: 60,
    effectiveness: 0.85,
    canBuildFireline: false,
    canDefendStructure: false,
    windSensitive: true,
    nightCapable: false,
  },
  DASH: {
    kind: UnitKind.DASH,
    category: UnitCategory.AERIEN,
    label: 'Dash',
    agent: Agent.RETARDANT,
    capacityM3: 10,
    speedKmh: 460,
    reloadSeconds: 900,
    enduranceSeconds: 16_200,
    hourlyCostEur: 9000,
    effectRadiusM: 80,
    effectiveness: 0.7,
    canBuildFireline: false,
    canDefendStructure: false,
    windSensitive: true,
    nightCapable: false,
  },
  HELICOPTERE: {
    kind: UnitKind.HELICOPTERE,
    category: UnitCategory.AERIEN,
    label: "Helicoptere bombardier d'eau",
    agent: Agent.EAU,
    capacityM3: 2.5,
    speedKmh: 200,
    reloadSeconds: 130,
    enduranceSeconds: 9000,
    hourlyCostEur: 4500,
    effectRadiusM: 35,
    effectiveness: 0.75,
    canBuildFireline: false,
    canDefendStructure: true,
    windSensitive: true,
    nightCapable: false,
  },
  HELICO_COMMANDEMENT: {
    kind: UnitKind.HELICO_COMMANDEMENT,
    category: UnitCategory.COMMANDEMENT,
    label: 'Helicoptere de commandement',
    agent: Agent.AUCUN,
    capacityM3: 0,
    speedKmh: 220,
    reloadSeconds: 0,
    enduranceSeconds: 9000,
    hourlyCostEur: 2500,
    effectRadiusM: 800,
    effectiveness: 0,
    canBuildFireline: false,
    canDefendStructure: false,
    windSensitive: false,
    nightCapable: true,
  },
  CCF: {
    kind: UnitKind.CCF,
    category: UnitCategory.TERRESTRE,
    label: 'Camion-citerne feux de forets',
    agent: Agent.EAU,
    capacityM3: 4,
    speedKmh: 45,
    reloadSeconds: 600,
    enduranceSeconds: 28_800,
    hourlyCostEur: 320,
    effectRadiusM: 40,
    effectiveness: 0.55,
    canBuildFireline: false,
    canDefendStructure: true,
    windSensitive: false,
    nightCapable: true,
  },
  CCGC: {
    kind: UnitKind.CCGC,
    category: UnitCategory.TERRESTRE,
    label: 'Camion-citerne grande capacite',
    agent: Agent.EAU,
    capacityM3: 12,
    speedKmh: 35,
    reloadSeconds: 1200,
    enduranceSeconds: 28_800,
    hourlyCostEur: 380,
    effectRadiusM: 30,
    effectiveness: 0.4,
    canBuildFireline: false,
    canDefendStructure: true,
    windSensitive: false,
    nightCapable: true,
  },
  GROUPE_SOL: {
    kind: UnitKind.GROUPE_SOL,
    category: UnitCategory.TERRESTRE,
    label: 'Groupe feux de forets',
    agent: Agent.EAU,
    capacityM3: 1,
    speedKmh: 25,
    reloadSeconds: 600,
    enduranceSeconds: 21_600,
    hourlyCostEur: 260,
    effectRadiusM: 30,
    effectiveness: 0.5,
    canBuildFireline: true,
    canDefendStructure: true,
    windSensitive: false,
    nightCapable: true,
  },
  BULLDOZER: {
    kind: UnitKind.BULLDOZER,
    category: UnitCategory.TERRESTRE,
    label: 'Bulldozer',
    agent: Agent.AUCUN,
    capacityM3: 0,
    speedKmh: 12,
    reloadSeconds: 0,
    enduranceSeconds: 25_200,
    hourlyCostEur: 220,
    effectRadiusM: 20,
    effectiveness: 0,
    canBuildFireline: true,
    canDefendStructure: false,
    windSensitive: false,
    nightCapable: true,
  },
  PC_MOBILE: {
    kind: UnitKind.PC_MOBILE,
    category: UnitCategory.COMMANDEMENT,
    label: 'Poste de commandement mobile',
    agent: Agent.AUCUN,
    capacityM3: 0,
    speedKmh: 40,
    reloadSeconds: 0,
    enduranceSeconds: 86_400,
    hourlyCostEur: 150,
    effectRadiusM: 1200,
    effectiveness: 0,
    canBuildFireline: false,
    canDefendStructure: false,
    windSensitive: false,
    nightCapable: true,
  },
};

export const AERIAL_UNIT_KINDS: readonly UnitKindId[] = Object.values(UNIT_SPECS)
  .filter((spec) => spec.category === UnitCategory.AERIEN)
  .map((spec) => spec.kind);

export const GROUND_UNIT_KINDS: readonly UnitKindId[] = Object.values(UNIT_SPECS)
  .filter((spec) => spec.category === UnitCategory.TERRESTRE)
  .map((spec) => spec.kind);
